import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaPaperPlane, FaCheckCircle, FaExclamationTriangle, FaCloudUploadAlt } from "react-icons/fa";
import { uploadClient } from "../api/config";

const PRIORITY_OPTIONS = [
  { key: "Low", dot: "#22c55e" },
  { key: "Medium", dot: "#f59e0b" },
  { key: "High", dot: "#fb923c" },
  { key: "Critical", dot: "#ef4444" },
];

const EMPTY_FIELD_ERRORS = { email: "", subject: "", message: "" };

const fieldBorder = (hasError) =>
  hasError ? "1px solid #ef4444" : "1px solid var(--color-border)";

const FieldError = ({ message }) => {
  if (!message) return null;
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", marginTop: 2 }}>
      {message}
    </div>
  );
};

const CreateTicketModal = ({ open, onClose, userEmail, clientName }) => {
  const fileInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const subjectInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("Low");
  const [email, setEmail] = useState(userEmail || "");
  const [attachment, setAttachment] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(""); // API / server errors only
  const [fieldErrors, setFieldErrors] = useState(EMPTY_FIELD_ERRORS);
  const [toast, setToast] = useState({ open: false, message: "", kind: "success" });

  const canSubmit = useMemo(() => !submitting, [submitting]);

  useEffect(() => {
    if (!open) return;

    setSuccess(false);
    setError("");
    setFieldErrors(EMPTY_FIELD_ERRORS);
    setSubmitting(false);
    setSubject("");
    setMessage("");
    setPriority("Low");
    setAttachment(null);
    setEmail(userEmail || "");
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setEmail((prev) => prev || userEmail || "");
  }, [open, userEmail]);

  // Clear each field-level error as soon as that field becomes valid.
  useEffect(() => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      if (prev.email && email.trim()) {
        next.email = "";
        changed = true;
      }
      if (prev.subject && subject.trim()) {
        next.subject = "";
        changed = true;
      }
      if (prev.message && message.trim()) {
        next.message = "";
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [email, subject, message]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const readField = (ref, fallback) => {
    const live = ref.current?.value;
    if (typeof live === "string") return live.trim();
    return String(fallback || "").trim();
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    setError("");
    setSuccess(false);

    const e = readField(emailInputRef, email);
    const s = readField(subjectInputRef, subject);
    const m = readField(messageInputRef, message);

    // Only mark empty required fields — filled fields get no error.
    const nextErrors = {
      email: e ? "" : "Please enter your email.",
      subject: s ? "" : "Please enter a subject.",
      message: m ? "" : "Please enter a message.",
    };
    setFieldErrors(nextErrors);
    setEmail(e);
    setSubject(s);
    setMessage(m);

    if (!e || !s || !m) {
      if (!e) emailInputRef.current?.focus();
      else if (!s) subjectInputRef.current?.focus();
      else messageInputRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("user_email", e);
      form.append("subject", s);
      form.append("description", m);
      form.append("priority", priority);
      form.append("client_name", clientName || "");
      if (attachment) {
        form.append("attachment", attachment, attachment.name);
      }

      await uploadClient.post("/api/support/submit-ticket/", form);

      setSuccess(true);
      setSubject("");
      setMessage("");
      setPriority("Low");
      setAttachment(null);
      setFieldErrors(EMPTY_FIELD_ERRORS);

      setToast({ open: true, message: "Ticket submitted to support.", kind: "success" });
      closeTimerRef.current = setTimeout(() => setToast((t) => ({ ...t, open: false })), 2800);
      onClose?.();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Unable to submit ticket. Please try again.";
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="create-ticket-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={handleClose}
        >
          <motion.div
            key="create-ticket-content"
            initial={{ opacity: 0, scale: 0.95, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 14 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            style={{
              width: "min(95vw, 720px)",
              maxHeight: "min(86vh, 760px)",
              background: "var(--color-bg-card)",
              color: "var(--color-text)",
              borderRadius: 12,
              border: "1px solid var(--color-border)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(111,66,193,0.14)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaPaperPlane size={12} color="#6f42c1" />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "var(--color-text)", lineHeight: 1.1 }}>Create Support Ticket</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 2 }}>Submit a ticket to our support team</div>
                </div>
              </div>

              <button
                onClick={handleClose}
                style={{
                  background: "#ffe4e6",
                  border: "1px solid #fecdd3",
                  color: "#e11d48",
                  padding: "6px 10px",
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
                disabled={submitting}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <FaTimes size={10} />
                  Close
                </span>
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              noValidate
              style={{
                padding: 18,
                display: "grid",
                gap: 14,
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                margin: 0,
              }}
            >
              {success && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "rgba(16,185,129,0.12)",
                    border: "1px solid rgba(16,185,129,0.25)",
                    color: "var(--color-text)",
                  }}
                >
                  <FaCheckCircle color="#10b981" />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Ticket submitted to support.</span>
                </div>
              )}

              {error && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "rgba(239,68,68,0.10)",
                    border: "1px solid rgba(239,68,68,0.25)",
                    color: "var(--color-text)",
                  }}
                >
                  <FaExclamationTriangle color="#ef4444" />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{error}</span>
                </div>
              )}

              <div style={{ display: "grid", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)" }}>Your Email *</label>
                <input
                  ref={emailInputRef}
                  name="user_email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  onInput={(ev) => setEmail(ev.target.value)}
                  placeholder="you@company.com"
                  aria-invalid={Boolean(fieldErrors.email)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: fieldBorder(fieldErrors.email),
                    background: "var(--color-bg-card)",
                    color: "var(--color-text)",
                    outline: "none",
                  }}
                />
                <FieldError message={fieldErrors.email} />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)" }}>Subject *</label>
                <input
                  ref={subjectInputRef}
                  name="subject"
                  type="text"
                  autoComplete="off"
                  value={subject}
                  onChange={(ev) => setSubject(ev.target.value)}
                  onInput={(ev) => setSubject(ev.target.value)}
                  placeholder="Brief summary of the issue"
                  aria-invalid={Boolean(fieldErrors.subject)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: fieldBorder(fieldErrors.subject),
                    background: "var(--color-bg-card)",
                    color: "var(--color-text)",
                    outline: "none",
                  }}
                />
                <FieldError message={fieldErrors.subject} />
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)" }}>Priority</label>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  {PRIORITY_OPTIONS.map((p) => {
                    const selected = priority === p.key;
                    return (
                      <div
                        key={p.key}
                        role="button"
                        tabIndex={0}
                        onClick={() => setPriority(p.key)}
                        onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") setPriority(p.key); }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          borderRadius: 999,
                          border: selected ? "1px solid #fca5a5" : "1px solid transparent",
                          background: selected ? "rgba(239,68,68,0.08)" : "transparent",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: p.dot, display: "inline-block" }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: selected ? "#ef4444" : "var(--color-text)" }}>{p.key}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)" }}>Message *</label>
                <textarea
                  ref={messageInputRef}
                  name="description"
                  value={message}
                  onChange={(ev) => setMessage(ev.target.value)}
                  onInput={(ev) => setMessage(ev.target.value)}
                  placeholder="Describe the issue, impacted users/systems, and any relevant context."
                  rows={6}
                  aria-invalid={Boolean(fieldErrors.message)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: fieldBorder(fieldErrors.message),
                    background: "var(--color-bg-card)",
                    color: "var(--color-text)",
                    outline: "none",
                    resize: "vertical",
                  }}
                />
                <FieldError message={fieldErrors.message} />
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)" }}>Attachment</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={(ev) => {
                    const f = ev.target.files?.[0] || null;
                    setAttachment(f);
                  }}
                />
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") fileInputRef.current?.click(); }}
                  onDragOver={(ev) => { ev.preventDefault(); }}
                  onDrop={(ev) => {
                    ev.preventDefault();
                    const f = ev.dataTransfer.files?.[0] || null;
                    setAttachment(f);
                  }}
                  style={{
                    border: "1px dashed var(--color-border)",
                    background: "var(--color-bg-card)",
                    borderRadius: 12,
                    padding: "18px 14px",
                    textAlign: "center",
                    color: "var(--color-text-light)",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 4 }}>
                    <FaCloudUploadAlt color="var(--color-text-light)" />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-light)" }}>
                      Click to browse or drag & drop a file
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--color-text-light)" }}>
                    Max 10MB · JPG, PNG, PDF, DOCX, TXT, ZIP
                  </div>
                  {attachment && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "var(--color-text)", fontWeight: 700 }}>
                      Selected: {attachment.name}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  paddingTop: 4,
                }}
              >
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid var(--color-border)",
                    background: "var(--color-bg-card)",
                    color: "var(--color-text-light)",
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid rgba(111,66,193,0.25)",
                    background: canSubmit ? "#6f42c1" : "rgba(111,66,193,0.45)",
                    color: "#fff",
                    cursor: canSubmit ? "pointer" : "not-allowed",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaPaperPlane size={13} />
                  {submitting ? "Submitting..." : "Submit Ticket"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {toast.open && (
        <motion.div
          key="create-ticket-toast"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed",
            top: 14,
            right: 14,
            zIndex: 100000,
            background: toast.kind === "success" ? "rgba(16,185,129,0.95)" : "rgba(239,68,68,0.95)",
            color: "#fff",
            borderRadius: 12,
            padding: "10px 12px",
            boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            maxWidth: 420,
            cursor: "pointer",
          }}
          onClick={() => setToast((t) => ({ ...t, open: false }))}
          role="status"
          aria-live="polite"
        >
          {toast.kind === "success" ? <FaCheckCircle /> : <FaExclamationTriangle />}
          <span style={{ fontSize: 13, fontWeight: 700 }}>{toast.message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateTicketModal;
